'use client';

import { useState, useEffect } from 'react';
import { ChevronRight } from 'lucide-react';
import { HorizontalCarousel } from './HorizontalCarousel';

const ACCENT = '#3d8b7a';

export interface ServeItem {
  Opportunity_ID: number;
  Opportunity_Title: string;
  Ministry_ID: number;
  Congregation_ID: number;
  Congregation_Name: string;
  Ministry_Name: string;
  Image_URL: string | null;
}

export interface ServeCarouselProps {
  /** Base URL for the API (the host serving /api/opportunities) */
  apiBaseUrl?: string;
  /** Campus filter — accepts a Campus_Slug or a numeric Congregation_ID */
  campus?: string;
  /** Ministry ID filter */
  ministryId?: number;
  /** Program ID filter — narrower than a ministry */
  programId?: number;
  /** Key-word filter, matched against opportunity title, program name, ministry name */
  search?: string;
  /**
   * @deprecated The in-widget "View All" button was removed; the host page
   * supplies its own button with prefiltered URL params. Accepted but ignored.
   */
  viewAllUrl?: string;
}

const MOCK_SERVE: ServeItem[] = [
  {
    Opportunity_ID: 1,
    Opportunity_Title: 'Student Ministry Volunteer',
    Ministry_ID: 1,
    Congregation_ID: 1,
    Congregation_Name: 'Algonac',
    Ministry_Name: 'Student Ministry',
    Image_URL: null,
  },
  {
    Opportunity_ID: 2,
    Opportunity_Title: 'Kids Ministry Helper',
    Ministry_ID: 2,
    Congregation_ID: 1,
    Congregation_Name: 'Algonac',
    Ministry_Name: 'Student Ministry',
    Image_URL: null,
  },
  {
    Opportunity_ID: 3,
    Opportunity_Title: 'Worship Team',
    Ministry_ID: 3,
    Congregation_ID: 1,
    Congregation_Name: 'Algonac',
    Ministry_Name: 'Student Ministry',
    Image_URL: null,
  },
  {
    Opportunity_ID: 4,
    Opportunity_Title: 'Greeting Team',
    Ministry_ID: 4,
    Congregation_ID: 2,
    Congregation_Name: 'Troy',
    Ministry_Name: 'Hospitality',
    Image_URL: null,
  },
];

// Vite bakes these at build time via `define` in vite.config.ts.
const BAKED_API_URL = process.env.CHURCH_HUB_API_URL ?? '';
const BAKED_API_KEY = process.env.CHURCH_HUB_API_KEY ?? '';
const BAKED_APP_SLUG = process.env.CHURCH_HUB_APP_SLUG ?? '';

export function ServeCarousel({
  apiBaseUrl = BAKED_API_URL,
  campus,
  ministryId,
  programId,
  search,
}: ServeCarouselProps) {
  const [opportunities, setOpportunities] = useState<ServeItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [usingMock, setUsingMock] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchOpportunities() {
      try {
        const params = new URLSearchParams();
        if (campus) params.set('campus', campus);
        if (ministryId) params.set('ministry_id', String(ministryId));
        if (programId) params.set('program_id', String(programId));
        if (search) params.set('search', search);
        const qs = params.toString();

        const headers: Record<string, string> = {};
        if (BAKED_API_KEY) headers['x-api-key'] = BAKED_API_KEY;
        if (BAKED_APP_SLUG) headers['x-app-slug'] = BAKED_APP_SLUG;
        const res = await fetch(`${apiBaseUrl}/api/opportunities${qs ? `?${qs}` : ''}`, {
          headers,
        });
        if (!res.ok) throw new Error(`${res.status}`);
        const data: ServeItem[] = await res.json();

        // An empty list is a real answer, not a misconfiguration — a filtered
        // embed (ministry/program/search) can legitimately have nothing open,
        // and showing sample opportunities there would publish fake ones.
        if (!cancelled) setOpportunities(data);
      } catch {
        if (!cancelled) {
          setOpportunities(MOCK_SERVE);
          setUsingMock(true);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetchOpportunities();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, campus, ministryId, programId, search]);

  // Nothing open for this filter — render nothing rather than an empty rail.
  if (!isLoading && opportunities.length === 0) return null;

  return (
    <section>
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-4xl leading-tight font-bold">Serve Opportunities</h2>
      </div>

      {/* Carousel */}
      <HorizontalCarousel fadeBg="#f3f3f3">
        {isLoading
          ? Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="w-[300px] flex-shrink-0 overflow-hidden sm:w-[380px] lg:w-[460px]"
              >
                <div className="aspect-video animate-pulse bg-gray-300" />
                <div
                  className="h-14 animate-pulse"
                  style={{ backgroundColor: ACCENT, opacity: 0.5 }}
                />
              </div>
            ))
          : opportunities.map((opp) => (
              <a
                key={opp.Opportunity_ID}
                href={`https://woodsidebible.org/serve/${opp.Opportunity_ID}/`}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-[300px] flex-shrink-0 overflow-hidden text-inherit no-underline shadow-md transition-all duration-300 hover:scale-[1.005] hover:shadow-xl sm:w-[380px] lg:w-[460px]"
              >
                {/* Image */}
                <div className="aspect-video bg-gray-200">
                  {opp.Image_URL ? (
                    <img
                      src={opp.Image_URL}
                      alt={opp.Opportunity_Title}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center bg-gradient-to-br from-gray-300 to-gray-400 px-4 text-center text-sm font-medium text-gray-500">
                      {opp.Opportunity_Title}
                    </div>
                  )}
                </div>

                {/* Teal footer */}
                <div
                  className="flex items-center justify-between px-5 py-4"
                  style={{ backgroundColor: ACCENT }}
                >
                  <span className="truncate text-sm font-semibold tracking-wider text-white uppercase">
                    {opp.Congregation_Name}
                    <span className="mx-2 opacity-60">|</span>
                    {opp.Opportunity_Title}
                  </span>
                  <ChevronRight className="ml-3 h-5 w-5 flex-shrink-0 text-white" />
                </div>
              </a>
            ))}
      </HorizontalCarousel>

      {usingMock && (
        <p className="mt-2 text-center text-xs text-gray-400">
          Sample data — configure API to show live opportunities
        </p>
      )}
    </section>
  );
}
